// Painted world v2 (Oct 15 2026; Valor/output/map-painted-world-v2, CLAUDE-HANDOFF.md): the runtime side of the second
// painted-art pass — bright castles per relationship and tier, their pennants, painted materials for the ground, lakes,
// streams and gorges, shore and gorge-wall relief, timber bridges, single trees, rocks and reeds.
//
// The art is cut from the package's atlases by art-src/map-painted-world-v2/export.py into public/art/map/painted-world-v2;
// its sizes and measured points are in paintedWorldData.ts (generated). This file holds the numbers that place the art
// on the map — so drawing and hit testing share them and the unit tests cover them — and the one-time loader. Nothing
// here runs per frame except the small pure geometry functions. Every piece is optional: whatever fails to load leaves
// the renderer drawing what it drew before (the Slate & Fire castles, the procedural terrain).
//
// Coordinates: image px are x right / y down; world cells are x east / y north (negate y to go between them).
import { art } from "@/kit/art";
import { PAINTED_WORLD_DATA as D } from "./paintedWorldData";
import { readBack, type Material } from "./materials";
import { type CastleBox, type CityRelation, type Tier } from "./slateFire";

// ------------------------------------------------------------------------------------------------ metadata types
export interface SpriteMeta {
  file: string;
  w: number;
  h: number;
  /** export scale: output px per master px (relative sizes between sprites of one sheet come from master px) */
  scale: number;
  /** painted bounds (alpha > 24) [left, top, right, bottom), output px */
  painted: readonly number[];
}
type CastleMeta = SpriteMeta & { contact: readonly number[]; roof: readonly number[]; solid: readonly number[] };
type BridgeMeta = SpriteMeta & { type: "river" | "rift"; angleDeg: number; centre: readonly number[]; ends: readonly (readonly number[])[]; span: number };
type SceneryMeta = SpriteMeta & { foot: readonly number[]; crowns?: number };
type ShoreMeta = SpriteMeta & { anchor: readonly number[]; tangentDeg: number; waterNormal: readonly number[]; length: number };
type WallMeta = SpriteMeta & { rimAnchor: readonly number[]; tangentDeg: number; rimLength: number; faceDepth: number; rimBend: number };

export const castleMeta = (rel: CityRelation, tier: Tier) => D.castles[`${rel}:${tier}`] as CastleMeta;
export const castleUrl = (rel: CityRelation, tier: Tier) => art.map.paintedWorld(castleMeta(rel, tier).file);

// ------------------------------------------------------------------------------------------------ castles
/** Painted width of a castle per tier, × s. The package's castles are already painted bright, so no display boost. */
export const PW_TIER_WIDTH: Record<Tier, number> = { 1: 0.62, 2: 0.7, 3: 0.78, 4: 0.86 };
/** The castle's foundation contact (the painted bounds' bottom centre, the package's estimate, checked by eye) stands this
 *  many cells south of the cell centre — where the Slate & Fire castles' painted foundations stood (0.25–0.28). */
export const PW_CONTACT_DY = 0.27;
/** No castle is painted taller than this, × s (a tier IV is 0.85). */
export const PW_MAX_PAINTED_H = 1.0;

/** A painted castle's rect: the whole exported image (so the glow, which is baked around the whole image, frames it as
 *  the Slate & Fire glows frame theirs), with the painted top / bottom fractions for nameplates and hit fallbacks. */
export interface PaintedBox extends CastleBox {
  paintTop: number;
  paintBottom: number;
  /** the exported image's px → CSS px */
  k: number;
}

export function paintedCastleBox(rel: CityRelation, tier: Tier, s: number, px: number, py: number): PaintedBox {
  const m = castleMeta(rel, tier);
  const pw = m.painted[2] - m.painted[0];
  const ph = m.painted[3] - m.painted[1];
  let k = (s * PW_TIER_WIDTH[tier]) / pw;
  if (ph * k > s * PW_MAX_PAINTED_H) k = (s * PW_MAX_PAINTED_H) / ph;
  const w = m.w * k;
  const h = m.h * k;
  const x = px - m.contact[0] * k;
  const y = py + s * PW_CONTACT_DY - m.contact[1] * k;
  return { x, y, w, h, cx: x + w / 2, baseY: y + h, paintTop: m.painted[1] / m.h, paintBottom: m.painted[3] / m.h, k };
}

/** Where the pennant's pole stands, normalised in the castle's image: on the highest spire (the package's roof estimate),
 *  a little into its tip so the pole rises from the roof rather than floating above it. */
export function paintedFlagMount(rel: CityRelation, tier: Tier): [number, number] {
  const m = castleMeta(rel, tier);
  return [m.roof[0] / m.w, (m.roof[1] + (m.painted[3] - m.painted[1]) * 0.05) / m.h];
}

/** A status shield's spot on the painted foundation, normalised: in from its east end, up from its base. */
export function paintedStatusMount(rel: CityRelation, tier: Tier): [number, number] {
  const m = castleMeta(rel, tier);
  const pw = m.painted[2] - m.painted[0];
  const ph = m.painted[3] - m.painted[1];
  return [(m.painted[2] - pw * 0.1) / m.w, (m.painted[3] - ph * 0.2) / m.h];
}

/** The pennant cloth's aspect (h / w) for a relationship. */
export function pennantAspect(rel: CityRelation): number {
  const m = D.pennants[rel] as SpriteMeta;
  return (m.painted[3] - m.painted[1]) / (m.painted[2] - m.painted[0]);
}

// ------------------------------------------------------------------------------------------------ bridges
/** The deck's direction on screen as an image angle (clockwise from east, mod 180) from a world direction (x east,
 *  y north). */
export function imageAngle(ux: number, uy: number): number {
  const a = (Math.atan2(-uy, ux) * 180) / Math.PI;
  return ((a % 180) + 180) % 180;
}
/** Smallest difference between two axis angles (mod 180), degrees. */
export function axisDiff(a: number, b: number): number {
  const d = Math.abs((((a - b) % 180) + 180) % 180);
  return Math.min(d, 180 - d);
}
/** A painted bridge is used only within this of the crossing's own axis; otherwise the drawn timber stands in. */
export const BRIDGE_TOLERANCE = 14;

/** The painted bridge for a crossing — the closest authored axis among that type's views — or null when none is within
 *  BRIDGE_TOLERANCE (the procedural deck is drawn). Ties go to the first listed (a deterministic choice). */
export function pickBridge(type: "river" | "rift", ux: number, uy: number): number | null {
  const want = imageAngle(ux, uy);
  let best = -1;
  let bestD = Infinity;
  D.bridges.forEach((b, i) => {
    if (b.type !== type) return;
    const d = axisDiff(b.angleDeg, want);
    if (d < bestD - 1e-9) { bestD = d; best = i; }
  });
  return best >= 0 && bestD <= BRIDGE_TOLERANCE ? best : null;
}

/** The painted bridge's rect: uniform scale so its measured ends (the abutment stones) span the crossing's deck plus a
 *  little bank at each side, its measured centre on the crossing's middle. */
export function bridgeRect(i: number, x: number, y: number, halfLength: number, s: number): { x: number; y: number; w: number; h: number; k: number } {
  const b = D.bridges[i] as BridgeMeta;
  const k = ((halfLength * 2 + 0.12) * s) / b.span;
  return { x: x - b.centre[0] * k, y: y - b.centre[1] * k, w: b.w * k, h: b.h * k, k };
}

// ------------------------------------------------------------------------------------------------ shores and gorge walls
/** A shore patch fitting a shoreline point whose water lies along the image direction (nx, ny): the patch whose own
 *  measured water side is closest, within `tol` degrees (null: none fits, the continuous painting alone shows there). */
export function pickShore(nx: number, ny: number, tol = 24): number | null {
  const want = Math.atan2(ny, nx);
  let best = -1;
  let bestD = Infinity;
  D.shores.forEach((m, i) => {
    const a = Math.atan2(m.waterNormal[1], m.waterNormal[0]);
    let d = Math.abs(a - want) % (Math.PI * 2);
    if (d > Math.PI) d = Math.PI * 2 - d;
    if (d < bestD) { bestD = d; best = i; }
  });
  return best >= 0 && (bestD * 180) / Math.PI <= tol ? best : null;
}
export const shoreMeta = (i: number) => D.shores[i] as ShoreMeta;

/** A gorge-wall patch for a gorge running along image axis `deg`: every authored wall shows its face to the south (the
 *  viewer), hanging below its rim, so it fits the gorge's northern wall; the closest rim axis within `tol`. */
export function pickWall(deg: number, tol = 13): number | null {
  let best = -1;
  let bestD = Infinity;
  D.walls.forEach((m, i) => {
    // a strongly bent (V-shaped) rim only fits a bend; keep those for gentle east–west runs
    const d = axisDiff(m.tangentDeg, deg) + (m.rimBend > 16 ? 3 : 0);
    if (d < bestD) { bestD = d; best = i; }
  });
  return best >= 0 && bestD <= tol ? best : null;
}
export const wallMeta = (i: number) => D.walls[i] as WallMeta;

// ------------------------------------------------------------------------------------------------ scenery
export const treeMeta = (i: number) => D.trees[i] as SceneryMeta;
export const rockMeta = (i: number) => D.rocks[i] as SceneryMeta;
export const reedMeta = (i: number) => D.reeds[i] as SceneryMeta;
/** The tallest tree (master px) is painted this many cells tall at full size: 0.27 s is 32% of a tier IV castle's painted
 *  height (the brief's 22–32%); the other trees keep their authored height against it. */
export const TREE_HEIGHT = 0.27;
/** The widest rock (master px) is this many cells wide — 15% of a tier IV castle's painted width (the brief's 8–16%). */
export const ROCK_WIDTH = 0.13;
export const REED_WIDTH = 0.12;
/** Master-px height of the tallest tree / width of the widest rock and reed clump (the reference for the sizes above). */
const masterH = (m: SpriteMeta) => (m.painted[3] - m.painted[1]) / m.scale;
const masterW = (m: SpriteMeta) => (m.painted[2] - m.painted[0]) / m.scale;
const TREE_REF = Math.max(...D.trees.map((m) => masterH(m as SpriteMeta)));
const ROCK_REF = Math.max(...D.rocks.map((m) => masterW(m as SpriteMeta)));
const REED_REF = Math.max(...D.reeds.map((m) => masterW(m as SpriteMeta)));

/** A scenery sprite's rect for its foot at (fx, fy) CSS px, at `scale` × its authored size. */
export function sceneryRect(kind: "tree" | "rock" | "reed", i: number, fx: number, fy: number, s: number, scale = 1): { x: number; y: number; w: number; h: number } {
  const m = kind === "tree" ? treeMeta(i) : kind === "rock" ? rockMeta(i) : reedMeta(i);
  const size = kind === "tree" ? TREE_HEIGHT : kind === "rock" ? ROCK_WIDTH : REED_WIDTH;
  const ref = kind === "tree" ? TREE_REF : kind === "rock" ? ROCK_REF : REED_REF;
  // px of this export per CSS px: master px → cells via the reference, then × s
  const k = ((size * s * scale) / ref) / m.scale;
  return { x: fx - m.foot[0] * k, y: fy - m.foot[1] * k, w: m.w * k, h: m.h * k };
}
/** Crowns a tree sprite carries (the two-pine sprite has two), for density accounting. */
export const treeCrowns = (i: number) => treeMeta(i).crowns ?? 1;
export const TREE_COUNT = D.trees.length;
export const ROCK_COUNT = D.rocks.length;
export const REED_COUNT = D.reeds.length;

// ------------------------------------------------------------------------------------------------ loading
export type PwMaterial = "lakeWater" | "riverWater" | "shoreGravel" | "gorgeWallLit" | "gorgeWallShadow" | "gorgeFloor";
const MATERIAL_IDS: Record<PwMaterial, keyof typeof D.materials> = {
  lakeWater: "lake-water", riverWater: "river-water", shoreGravel: "shore-gravel",
  gorgeWallLit: "gorge-wall-lit", gorgeWallShadow: "gorge-wall-shadow", gorgeFloor: "gorge-floor",
};
export const MEADOW_MEAN = D.materials["meadow-ground"].mean as readonly number[];
/** The meadow's tone: multiplied over the swatch once as it loads. */
export const MEADOW_TONE = "rgb(226,226,232)";

export interface PaintedWorld {
  /** sampled absolutely (a zero mean): materialDelta(m, …) returns the painted colour itself */
  materials: Record<PwMaterial, Material | null>;
  /** the meadow swatch mirrored 2 × 2 into one seamless image spanning MATERIAL_PERIOD (4) cells (an ImageBitmap where
   *  the browser has one: a pattern from a canvas is re-copied every time it is made, several times a ground tile) */
  meadow: ImageBitmap | HTMLCanvasElement | null;
  trees: (HTMLImageElement | null)[];
  rocks: (HTMLImageElement | null)[];
  reeds: (HTMLImageElement | null)[];
  shores: (HTMLImageElement | null)[];
  walls: (HTMLImageElement | null)[];
  bridges: (HTMLImageElement | null)[];
  pennants: Record<CityRelation, HTMLImageElement | null>;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const im = new Image();
    im.decoding = "async";
    im.onload = () => resolve(im);
    im.onerror = () => reject(new Error(`image failed: ${src}`));
    im.src = src;
  });
}

/** The swatch and its mirrors in one canvas: [A | A mirrored] over [A flipped | A rotated], as mirrorUV samples it. */
function mirrored(im: HTMLImageElement): HTMLCanvasElement | null {
  try {
    const n = im.naturalWidth;
    const c = document.createElement("canvas");
    c.width = c.height = n * 2;
    const g = c.getContext("2d");
    if (!g) return null;
    for (const [sx, sy] of [[1, 1], [-1, 1], [1, -1], [-1, -1]] as const) {
      g.setTransform(sx, 0, 0, sy, sx < 0 ? n * 2 : 0, sy < 0 ? n * 2 : 0);
      g.drawImage(im, 0, 0, n, n);
    }
    // a touch darker, toward the reference's meadow (its mean ~141, 126, 42 against the swatch's 159, 142, 34)
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.globalCompositeOperation = "multiply";
    g.fillStyle = MEADOW_TONE;
    g.fillRect(0, 0, n * 2, n * 2);
    return c;
  } catch {
    return null;
  }
}

/** The canvas as an ImageBitmap (then the canvas is released), or the canvas itself where there are none. */
async function asBitmap(c: HTMLCanvasElement | null): Promise<ImageBitmap | HTMLCanvasElement | null> {
  if (!c || typeof createImageBitmap !== "function") return c;
  try {
    const b = await createImageBitmap(c);
    c.width = c.height = 0;
    return b;
  } catch {
    return c;
  }
}

let loading: Promise<PaintedWorld> | null = null;

/** Everything but the castles (fetched as the map shows them), once per page. Each piece is optional. */
export function loadPaintedWorld(): Promise<PaintedWorld> {
  if (loading) return loading;
  const url = (f: string) => art.map.paintedWorld(f);
  const img = (f: string) => loadImage(url(f)).catch(() => null);
  const mat = (id: keyof typeof D.materials) => loadImage(url(D.materials[id].file)).then((im) => readBack(im, [0, 0, 0])).catch(() => null);
  const names = Object.keys(MATERIAL_IDS) as PwMaterial[];
  loading = Promise.all([
    Promise.all(names.map((n) => mat(MATERIAL_IDS[n]))),
    loadImage(url(D.materials["meadow-ground"].file)).then(mirrored).then(asBitmap).catch(() => null),
    Promise.all(D.trees.map((m) => img(m.file))),
    Promise.all(D.rocks.map((m) => img(m.file))),
    Promise.all(D.reeds.map((m) => img(m.file))),
    Promise.all(D.shores.map((m) => img(m.file))),
    Promise.all(D.walls.map((m) => img(m.file))),
    Promise.all(D.bridges.map((m) => img(m.file))),
    Promise.all((Object.keys(D.pennants) as CityRelation[]).map((r) => img(D.pennants[r].file).then((im) => [r, im] as const))),
  ]).then(([mats, meadow, trees, rocks, reeds, shores, walls, bridges, pennants]) => ({
    materials: Object.fromEntries(names.map((n, i) => [n, mats[i]])) as Record<PwMaterial, Material | null>,
    meadow, trees, rocks, reeds, shores, walls, bridges,
    pennants: Object.fromEntries(pennants) as Record<CityRelation, HTMLImageElement | null>,
  }));
  return loading;
}

